import React, { useState, useEffect } from "react";
import debounce from "debounce";
import * as notify from "shared/notify";
import LayoutSelector from "./layout-selector";
import { NO_FOCUS_STEAL_ELEMENT_NAME_LIST } from "../lib/constants";
import eventEmitter from "../../../shared/events";
import MeasurementLine from "./measurement-line";
import MeasuringAttributes from "./measuring-attributes";
import { useMeasurements } from "components/shipment-editor/measurements/hooks/use-measurements";

function MeasurementTab({ editOnly, sourcePage, position, isSelected, wipKeys, codingWipKey, renderAttributes = true, disabled }) {
    const { singleHighlightedCodingWipKey, measurementState, saveMeasurement, addMeasurement } = useMeasurements();
    const isCompleted = measurementState.isCompleted;
    const measurements = measurementState.measurements;
    const [selectedRow, setSelectedRow] = useState(null);
    const [imperialMeasurements, setImperialMeasurements] = useState([]);
    const [metricMeasurements, setMetricMeasurements] = useState([]);

    useEffect(() => {
        const positionMeasurements = measurements.filter((row) => row.fullPosition.id === position.id);
        setImperialMeasurements(positionMeasurements.filter((row) => row.type === "Imperial"));
        setMetricMeasurements(positionMeasurements.filter((row) => row.type === "Metric"));
    }, [measurements]);

    const selectMeasurementRow = (row) => {
        setSelectedRow(row);
    };

    useEffect(() => {
        if (selectedRow && selectedRow.key) {
            setTimeout(() => {
                const inputs = document.querySelectorAll(`#measurement_line_${selectedRow.key.join("-")} input`);
                const splitName = document.activeElement.name ? document.activeElement.name.split("_") : [];
                const activeElementName = splitName.length === 2 ? splitName[1] : document.activeElement.name;

                if (inputs[0] && (!activeElementName || !NO_FOCUS_STEAL_ELEMENT_NAME_LIST.includes(activeElementName))) {
                    inputs[0].focus();
                }
            }, 1);
        }
    }, [selectedRow]);

    useEffect(() => {
        if (isSelected) {
            setSelectedRow(null);
        }
    }, [isSelected]);

    useEffect(() => {
        const measurementList = [...imperialMeasurements, ...metricMeasurements];

        if (measurementList.length === 0) {
            return;
        }

        if (!selectedRow) {
            setSelectedRow(measurementList[0]);
        } else {
            setSelectedRow(measurementList.find((m) => m.key.join("-") === selectedRow.key.join("-")));
        }
    }, [imperialMeasurements, metricMeasurements, selectedRow]);

    const rowCount = measurements.length;

    const imperialMeasurementLines = imperialMeasurements.map((row, index) => {
        return (
            <MeasurementLine
                key={`imperial${row.positionValue}_${index}of${rowCount}`}
                editOnly={editOnly}
                sourcePage={sourcePage}
                data={row}
                wipKeys={wipKeys}
                type="Imperial"
                isSelected={!selectedRow ? false : selectedRow.key.join("-") === row.key.join("-")}
                selectMeasurementRow={selectMeasurementRow}
                disabled={disabled}
            />
        );
    });
    const metricMeasurementLines = metricMeasurements.map((row, index) => {
        return (
            <MeasurementLine
                key={`metric${row.positionValue}_${index}of${rowCount}`}
                editOnly={editOnly}
                sourcePage={sourcePage}
                data={row}
                wipKeys={wipKeys}
                type="Metric"
                isSelected={!selectedRow ? false : selectedRow.key.join("-") === row.key.join("-")}
                selectMeasurementRow={selectMeasurementRow}
                disabled={disabled}
            />
        );
    });

    const handleAddMeasurement = (type) => async () => {
        const upcNotSelected = wipKeys.length === 0 && !singleHighlightedCodingWipKey;

        if (upcNotSelected) {
            notify.error("UPC selection is required when adding measurements.");

            return;
        }

        const newMeasurement = await addMeasurement(measurementState, type, position);
        selectMeasurementRow(newMeasurement);
        eventEmitter.emit("REFRESH_TASK_STATUS");
    };

    const measurementList = [...imperialMeasurements, ...metricMeasurements];

    return (
        <div className="measurements-container">
            <div className="tabbed-measurements column -span12">
                <h3>{position.text} Measurements:</h3>
                {selectedRow && measurementList.length > 0 && (
                    <LayoutSelector
                        sourcePage={sourcePage}
                        updateMeasurement={saveMeasurement}
                        selectedMeasurement={selectedRow}
                    />
                )}
                <h3>Imperial</h3>
                {imperialMeasurementLines}
                {!isCompleted && (
                    <button
                        className="button -primary"
                        onClick={debounce(handleAddMeasurement("Imperial"), 500)}
                        disabled={imperialMeasurementLines.length > 0 || disabled}
                    >
                        + Imperial
                    </button>
                )}
                <h3>Metric</h3>
                {metricMeasurementLines}
                {!isCompleted && (
                    <button
                        className="button -primary"
                        onClick={debounce(handleAddMeasurement("Metric"), 500)}
                        disabled={metricMeasurementLines.length > 0 || editOnly || disabled}
                    >
                        + Metric
                    </button>
                )}
            </div>

            {renderAttributes && (
                <div className="column -span6">
                    <h3 className="m-t-0">Measuring Attributes</h3>
                    <MeasuringAttributes
                        groupId={position.attributeGroup}
                        showValidation={false}
                        isCompleted={isCompleted || disabled}
                        positionName={position.text}
                        codingWipKey={codingWipKey}
                    />
                </div>
            )}
        </div>
    );
}

export default MeasurementTab;
